import { Box } from '@jfs/ui-new'
import { defaultColors } from '@jfs/ui-new/src/theme/DefaultTheme'
import { useIsLargeScreen } from '@jfs/ui-new/src/utils/utils'
import { useNavigation } from '@react-navigation/native'
import { useEffect, useRef, useState } from 'react'
import { RefreshControl as RefreshControlMobile, ScrollView } from 'react-native'
import { RefreshControl as RefreshControlMobileWeb } from 'react-native-web-refresh-control'
import { ImageWithInfoSerializer } from '../../comps/ImageWithInfoSerializer/ImageWithInfoSerializer'
import { ListWithDateRange } from '../../comps/ListWithDateRange'
import { ShimmerView } from '../../comps/ShimmerView'
import { ToastPosition, ToastType } from '../../comps/Toast'
import { actionMenuStrings, staticStrings, transactionScreenStrings } from '../../data/static/strings'
import { RootStore, useStores } from '../../data/store'
import { Routes } from '../../nav/routes'
import { INFINITE_SCROLL_WINDOW, INFINITE_SCROLL_WINDOW_INDEX } from '../../util/common'
import {
  getErrorState,
  getLoadingState,
  getPrevDate,
  getSelectedDate,
  isMobile,
  isOsMobile,
  logEvent,
  tableConfig,
} from '../../util/util'
import { getFilterList } from './helper'
import { SearchBarWithFilters } from './SearchBarWithFilters'

const RefundedTransactionNative = ({
  fetchRefunds,
  refundApiHandlers,
  refundApiLoadMoreHandlers,
  fetchRefundsOnLoadMore,
}: any) => {
  const navigation = useNavigation()
  const [isLoadingMore, setLoadMore] = useState(false)

  const {
    mid,
    fetchDataForTransactionTab,
    resetPaginationTxn,
    moreTxnAvailable,
    selectedDateInfo,
    loadMoreRefund,
    fetchRefundDetailsOfPath,
    onRefundRetry,
    // settlementAccount,
    // isSettlementAccountAdded,
    updateSelectedTxn,
    canViewTransactionDetails,
    paginationRefunded,
    updatePagination,
    isMaxDateRangeSelected,
    updateSelectedDateInfo,
    paginationResetToZero,
    filtersData,
    clearRefundFilters,
  } = useStores((store) => ({
    mid: store.user.selectedMid,
    fetchDataForTransactionTab: store.transactions.fetchDataForTransactionTab,
    resetPaginationTxn: store.transactions.resetPaginationNativeRefunded,
    moreTxnAvailable: store.moreTxnAvailable,
    selectedDateInfo: store.transactions.refundedSelectedDateInfo,
    loadMoreRefund: store.loadMoreRefund,
    fetchRefundDetailsOfPath: store.fetchRefundDetailsOfPath,
    onRefundRetry: store.onRefundRetry,
    // settlementAccount: store.user.settlementAccount,
    // isSettlementAccountAdded: store?.user?.isSettlementAccountAdded(),
    length: store.transactions.refunded.length,
    updateSelectedTxn: store.transactions.updateSelectedTxn,
    canViewTransactionDetails: store.userManagement.canViewTransactionDetails(),
    paginationRefunded: store.transactions.paginationRefunded,
    updatePagination: store.transactions.updatePaginationNativeRefunded,
    isMaxDateRangeSelected: store.isMaxDateRangeSelected,
    updateSelectedDateInfo: store.transactions.updateRefundedSelectedDateInfo,
    paginationResetToZero: store.transactions.resetPaginationNativeRefundedToZero,
    filtersData: store.transactions.refundFilter.getFilterData,
    clearRefundFilters: store.transactions.refundFilter.clearFilters,
  }))

  const abortControllerRef = useRef<AbortController>()
  const canFetchMoreTxns = moreTxnAvailable(Routes.REFUNDS)
  const refundedTransactionList = fetchDataForTransactionTab(tableConfig(Routes.REFUNDS).tabletype)

  const loadingState = getLoadingState(refundApiHandlers)
  const loadMoreLoadingState = getLoadingState(refundApiLoadMoreHandlers)
  const errorState = getErrorState(refundApiHandlers)
  const loadMoreErrorState = getErrorState(refundApiLoadMoreHandlers)
  const showEmptyImage = refundedTransactionList?.length === 0 && !loadingState && !errorState
  const isLargeScreen = useIsLargeScreen()
  const ismobile = isMobile(isLargeScreen)
  const filtersList = getFilterList(filtersData)
  const [loadMoreRetryVisible, setLoadMoreRetryVisible] = useState(false)
  const layoutRef: any = useRef(null)

  useEffect(() => {
    setLoadMoreRetryVisible(loadMoreErrorState)
  }, [refundApiLoadMoreHandlers?.[1]?.state])

  useEffect(() => {
    if (mid) {
      abortControllerRef?.current?.abort()
      abortControllerRef.current = new AbortController()
      fetchRefundDetailsOfPath(fetchRefunds, selectedDateInfo, ismobile, abortControllerRef?.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchRefundDetailsOfPath, fetchRefunds, mid, ismobile])

  const filterDateSelected = filtersData?.selectedDateInfo?.fromDate && filtersData?.selectedDateInfo?.toDate
  const viewOlderRecords =
    !isMaxDateRangeSelected(selectedDateInfo) &&
    selectedDateInfo?.dateLabel === actionMenuStrings.infiniteScrollRange &&
    !filterDateSelected

  const loadPrevMonthRecords = () => {
    if (viewOlderRecords) {
      abortControllerRef?.current?.abort()
      abortControllerRef.current = new AbortController()
      fetchRefundDetailsOfPath(
        fetchRefunds,
        {
          fromDate: new Date(getPrevDate(new Date(selectedDateInfo?.fromDate), 30)),
          toDate: new Date(getPrevDate(new Date(selectedDateInfo?.toDate), 30)),
          dateLabel: actionMenuStrings.infiniteScrollRange,
        },
        ismobile,
        abortControllerRef?.current,
      )
      updateSelectedDateInfo({
        fromDate: new Date(getPrevDate(new Date(selectedDateInfo?.fromDate), 30)),
        toDate: new Date(getPrevDate(new Date(selectedDateInfo?.toDate), 30)),
        dateLabel: actionMenuStrings.infiniteScrollRange,
      })
    }
  }

  useEffect(() => {
    if (!loadingState && isLoadingMore) {
      setLoadMore(false)
    }
  }, [isLoadingMore, loadingState])
  const onLoadMore = (isRetry?: boolean) => {
    setLoadMoreRetryVisible(false)
    if (!isRetry && !canFetchMoreTxns && !isMaxDateRangeSelected(selectedDateInfo)) {
      const dateObj = {
        toDate: selectedDateInfo?.fromDate?.getTime(),
        fromDate: getPrevDate(selectedDateInfo?.fromDate, INFINITE_SCROLL_WINDOW),
        dateLabel: actionMenuStrings.infiniteScrollRange,
      }
      updateSelectedDateInfo(dateObj)
      paginationResetToZero()
    }
    setLoadMore(true)
    abortControllerRef?.current?.abort()
    abortControllerRef.current = new AbortController()
    loadMoreRefund(fetchRefundsOnLoadMore, ismobile, abortControllerRef?.current)
  }

  const onRefresh = () => {
    updateSelectedDateInfo({
      fromDate: getSelectedDate(INFINITE_SCROLL_WINDOW_INDEX),
      toDate: new Date(Date.now()),
      dateLabel: actionMenuStrings?.infiniteScrollRange,
    })
    resetPaginationTxn()
    abortControllerRef?.current?.abort()
    abortControllerRef.current = new AbortController()
    fetchRefundDetailsOfPath(fetchRefunds, selectedDateInfo, ismobile, abortControllerRef?.current)
  }

  const getEmptyMessage = (): string => {
    if (!loadingState) {
      return refundedTransactionList.length === 0 ? transactionScreenStrings.noRecords : transactionScreenStrings.empty
    }
    return ''
  }

  const handleRetry = (): void => {
    setLoadMoreRetryVisible(false)
    let dateObject = selectedDateInfo
    if (filtersData?.selectedDateInfo?.fromDate && filtersData?.selectedDateInfo?.toDate) {
      console.log('consider filter dates until clear filter')
      dateObject = filtersData?.selectedDateInfo
    }
    abortControllerRef?.current?.abort()
    abortControllerRef.current = new AbortController()
    onRefundRetry(fetchRefunds, ismobile, dateObject, abortControllerRef?.current)
  }

  const handleItemPress = (item: any) => {
    logEvent('item_press', 'refunded_payments')
    if (canViewTransactionDetails) {
      updateSelectedTxn(item.id)
      navigation.navigate(Routes.TRANSACTIONS_DETAILS, {
        transactionId: item?.originalTransactionId,
        refundAmount: item?.refundAmount,
        path: Routes.TRANSACTIONS,
        tab: 'refunded',
      })
    } else {
      RootStore?.response?.setCustomToast({
        message: staticStrings.notAuthorized,
        color: defaultColors.RED,
        position: ToastPosition.BOTTOM,
        toastType: ToastType.NEUTRAL,
        minWidthOfLeftBox: 200,
      })
    }
  }

  const RefreshControl = isOsMobile() ? RefreshControlMobile : RefreshControlMobileWeb
  const isEmptyView = showEmptyImage || (errorState && refundedTransactionList.length === 0)
  const dateFilterSelected =
    filtersData?.selectedDateInfo?.fromDate && filtersData?.selectedDateInfo?.toDate ? true : false

  const emptyView = () => {
    return (
      <ScrollView
        contentContainerStyle={{ flexGrow: 1, justifyContent: 'center', alignItems: 'center' }}
        refreshControl={<RefreshControl refreshing={false} onRefresh={onRefresh} />}>
        <ImageWithInfoSerializer
          show={showEmptyImage}
          screenName={Routes.REFUNDS}
          actionButtonVisible={viewOlderRecords}
          actionButtonName={'View older Records'}
          onPress={loadPrevMonthRecords}
        />
      </ScrollView>
    )
  }

  const resetFilters = () => {
    setLoadMoreRetryVisible(false)
    const defaultDateObj = {
      fromDate: getSelectedDate(INFINITE_SCROLL_WINDOW_INDEX),
      toDate: new Date(Date.now()),
      position: {
        top: 0,
        left: 0,
        visible: false,
      },
      dateLabel: actionMenuStrings?.infiniteScrollRange,
    }
    clearRefundFilters()
    updateSelectedDateInfo(defaultDateObj)
  }

  return (
    <>
      <SearchBarWithFilters
        filterList={filtersList}
        filtersData={filtersData}
        clearLoadMoreError={resetFilters}
        onApply={() => {
          let dateObject = selectedDateInfo
          setLoadMoreRetryVisible(false)
          if (filtersData?.selectedDateInfo?.fromDate && filtersData?.selectedDateInfo?.toDate) {
            console.log('consider filter dates until clear filter')
            dateObject = filtersData?.selectedDateInfo
          }
          abortControllerRef?.current?.abort()
          abortControllerRef.current = new AbortController()
          layoutRef.current = { y: 1 }
          fetchRefundDetailsOfPath(fetchRefunds, dateObject, ismobile, abortControllerRef)
        }}
        index={1}
      />
      <Box
        f={1}
        overflow={isOsMobile() ? undefined : 'auto'}
        bg={'WHITE'}
        align={isEmptyView || errorState ? 'center' : undefined}
        justify={isEmptyView || errorState ? 'center' : 'flex-start'}>
        <ImageWithInfoSerializer
          show={errorState}
          screenName={'errorTxn'}
          actionButtonVisible={true}
          actionButtonName={'Try Again'}
          onPress={handleRetry}
        />
        <ListWithDateRange
          data={refundedTransactionList}
          refreshControl={<RefreshControl refreshing={false} onRefresh={onRefresh} />}
          handleItemPress={handleItemPress}
          canFetchMoreTxns={canFetchMoreTxns}
          path={Routes.REFUNDS}
          isMaxDateRangeSelected={dateFilterSelected ? true : isMaxDateRangeSelected(selectedDateInfo)}
          isSeeAllVisible={false}
          loadingState={isLoadingMore}
          loadMoreErrorState={loadMoreRetryVisible}
          showShimmerView={loadMoreLoadingState}
          hide={errorState || (loadingState && !isLoadingMore)}
          onListFooterPress={onLoadMore}
          newDataFetch={layoutRef?.current}
          layoutRef={layoutRef}
          listEmptyComponent={emptyView}
        />
        {loadingState && !isLoadingMore && <ShimmerView type={Routes.TRANSACTIONS} />}
      </Box>
    </>
  )
}
export { RefundedTransactionNative }
